import { useRef, useState } from "react";
import { Upload } from "lucide-react";
import { api } from "@/lib/api";
import { useQuery } from "@/lib/useQuery";
import type { Contact, Doc } from "@/lib/types";
import { shortDate, titleCase } from "@/lib/format";
import { Button, Card, Loading, Empty, Tag, AiTag, toast } from "@/components/ui";
import { DocModal } from "./DocModal";

export function DocsTab({ scope, contacts, refetch: refetchOwner }: { scope: { accountId?: string; partnerId?: string }; contacts: Contact[]; refetch: () => void }) {
  const id = scope.accountId ?? scope.partnerId ?? "";
  const { data, loading, refetch } = useQuery<Doc[]>(`docs:${id}`, () => api.get<Doc[]>(scope.accountId ? `/documents?accountId=${scope.accountId}` : `/documents?partnerId=${scope.partnerId}`), [id]);
  const [busy, setBusy] = useState(false);
  const [category, setCategory] = useState("upload");
  const fileRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState<string | null>(null);
  const upload = async (file: File) => {
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    if (scope.accountId) form.append("accountId", scope.accountId);
    if (scope.partnerId) form.append("partnerId", scope.partnerId);
    form.append("category", category);
    try { await api.upload("/documents/upload", form); toast(`Uploaded ${file.name}`); refetch(); refetchOwner(); } catch (e) { toast((e as Error).message, true); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <Card title="Documents" actions={<>
      <select className="select" style={{ width: 150, height: 28 }} value={category} onChange={(e) => setCategory(e.target.value)}>
        <option value="upload">Upload</option><option value="contract">Contract</option><option value="agreement">Agreement</option>
      </select>
      <input ref={fileRef} type="file" hidden onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
      <Button size="md" variant="primary" disabled={busy} onClick={() => fileRef.current?.click()}><Upload size={13} /> Upload</Button>
    </>}>
      {loading && !data ? <Loading /> : (data ?? []).length === 0 ? <Empty>{scope.partnerId ? "No documents yet. Upload the partner agreement, slide decks, one-pagers or any file." : "No documents yet. Upload a contract, SOW or any file."}</Empty> : (
        <table className="table">
          <thead><tr><th>Title</th><th>Category</th><th>Version</th><th>Status</th><th>Size</th><th>Added</th><th></th></tr></thead>
          <tbody>
            {(data ?? []).map((d) => (
              <tr key={d.id} className="click" onClick={() => setOpen(d.id)}>
                <td style={{ fontWeight: 500 }} className="row gap-8">{d.title}{d.aiGenerated && <AiTag />}</td>
                <td><Tag color="outline">{titleCase(d.category)}</Tag></td>
                <td className="num muted">v{d.version}</td>
                <td className="muted-2">{titleCase(d.status)}{d.signedPdfSha256 && <span className="xs muted mono" title={d.signedPdfSha256}> · sha256 {d.signedPdfSha256.slice(0, 8)}</span>}</td>
                <td className="num muted">{d.size ? `${(d.size / 1024).toFixed(0)} KB` : "—"}</td>
                <td className="muted small">{shortDate(d.createdAt)}</td>
                <td className="right" onClick={(e) => e.stopPropagation()}>
                  {d.key && <a href={`/api/documents/${d.id}/file`} target="_blank" rel="noreferrer" className="small">Open</a>}
                  {d.signedPdfKey && <> · <a href={`/api/documents/${d.id}/file?signed=1`} target="_blank" rel="noreferrer" className="small">Signed PDF</a></>}
                  {d.body && !d.key && <button className="btn-link small" onClick={() => setOpen(d.id)}>Review</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {open && <DocModal id={open} contacts={contacts} onClose={() => setOpen(null)} onChanged={() => { refetch(); refetchOwner(); }} />}
    </Card>
  );
}
